import { Injectable, Injector } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { Observable, Subject, of } from "rxjs";
import { tap } from "rxjs/operators";

import { ToastrService } from "ngx-toastr";
import { AuthData } from "./auth-data.model";
import { FcmService } from "src/app/fcm.service";
import { MatDialog } from "@angular/material/dialog";
import { ProfileImageDialogComponent } from "./profile-image-dialog/profile-image-dialog.component";
import { SessionExpiredDialogComponent } from "./session-expired-dialog/session-expired-dialog.component";
import { ConfigService } from "src/app/services/config.service";
import { NavigationService } from "src/@vex/services/navigation.service";

export interface ConsentValidateResponse {
  partyType: string;
  name: string;
  fullName: string;
  email: string;
  firm: string | null;
  recipientName: string | null;
}

/** Master record that owns a consent invite (Attorney / Insurance databases). */
export interface ConsentPermalinkMasterRef {
  attorneyId?: string;
  insuranceId?: string;
  applicantId?: string;
}

@Injectable({ providedIn: "root" })
export class AuthService {
  private fcmToken: string;
  private isAuthenticated = false;
  private token: string;
  private tokenTimer: any;
  private userId: string;
  private username: string;
  private roleName: string;
  private permission: any;
  private authStatusListener = new Subject<boolean>();
  isLoading: boolean;
  public isLoadingsubject = new Subject<boolean>();
  email: string;
  private isPasswordChangeRequired = false;
  private pendingProfileImageCheck = false;
  /** Emitted whenever the active role is switched so live components can refresh. */
  public roleChanged$ = new Subject<void>();
  private imageUpdatedSource = new Subject<void>();
  imageUpdated$ = this.imageUpdatedSource.asObservable();
  BACKEND_URL: string;
  private inactivityTimer: any;
  private inactivityTimeoutMs = 60 * 60 * 1000; // 1 hour
  private lastStorageWrite = 0;
  private storageWriteThrottleMs = 10000; // 10 seconds
  private cleanupListeners: () => void;
  private sessionExpiredInteractionCleanup: () => void;
  private sessionExpiredDialogOpen = false;
  private static readonly SESSION_EXPIRED_KEY = "sessionExpiredDueToInactivity";
  public static readonly SESSION_INACTIVITY_ERROR_CODE = "SESSION_INACTIVITY_EXPIRED";
  private static readonly SESSION_EXPIRED_MESSAGE =
    "Your session was inactive for more than 1 hour and has expired. Please sign in again to continue using the application.";


  constructor(
    private http: HttpClient,
    private router: Router,
    private toastr: ToastrService,
    private fcmService: FcmService,
    private dialog: MatDialog,
    private injector: Injector,
    private configService: ConfigService,
    private navigationService: NavigationService,
  ) { }

  notifyImageUpdated() {
    this.imageUpdatedSource.next();
  }

  getToken() {
    return this.token;
  }

  getIsAuth() {
    const isAuth = localStorage.getItem("isAuthenticated") === "true";
    this.isAuthenticated = isAuth;
    return this.isAuthenticated;
  }

  getUserId() {
    return this.userId;
  }

  getuserName() {
    return this.username;
  }

  getUserEmail() {
    return this.email;
  }

  getRoleName() {
    return this.roleName || localStorage.getItem("roleName") || "";
  }

  getIsPasswordChangeRequired() {
    if (localStorage.getItem("isPasswordChangeRequired") === "true") {
      this.isPasswordChangeRequired = true;
    }
    return this.isPasswordChangeRequired;
  }

  getBackendURL() {
    const apiUrl = this.configService.get('apiUrl');
    if (apiUrl) {
      const newBackendUrl = apiUrl + '/admin/';
      if (this.BACKEND_URL !== newBackendUrl) {
        this.BACKEND_URL = newBackendUrl;
      }
    } else if (!this.BACKEND_URL) {
      // Fallback to default backend URL if config not loaded yet
      const defaultBackendUrl = 'http://localhost:8081/api/v1/oqme';
      this.BACKEND_URL = defaultBackendUrl + '/admin/';
    }
    return this.BACKEND_URL;
  }

  getAuthStatusListener() {
    return this.authStatusListener.asObservable();
  }

  getNotifications() {
    if (this.configService.areNotificationsDisabled()) {
      return of([]);
    }
    return this.http.get(`${this.getBackendURL()}notifications`);
  }

  getUnreadCount() {
    if (this.configService.areNotificationsDisabled()) {
      return of({
        success: true,
        unreadCount: 0,
        timestamp: new Date().toISOString(),
      });
    }
    return this.http.get<{ success: boolean; unreadCount: number; timestamp: string }>(
      `${this.getBackendURL()}notifications/unread-count`
    );
  }

  markAsRead(notificationId: string) {
    if (this.configService.areNotificationsDisabled()) {
      return of({ success: true });
    }
    return this.http.put(
      `${this.getBackendURL()}notifications/${notificationId}/mark-as-read`,
      {}
    );
  }

  markAllAsRead() {
    if (this.configService.areNotificationsDisabled()) {
      return of({ success: true });
    }
    return this.http.put(`${this.getBackendURL()}notifications/mark-all-as-read`, {});
  }

  markAllAsDelete() {
    if (this.configService.areNotificationsDisabled()) {
      return of({ success: true });
    }
    return this.http.delete(`${this.getBackendURL()}delete_notifications`, {});
  }


  login(email: string, password: string, rememberMe: boolean) {
    const authData = { email, password };
    console.log("Login URL:", this.getBackendURL() + "login");
    this.http
      .post<{ status: string; message: string }>(
        this.getBackendURL() + "login",
        authData
      )
      .subscribe({
        next: (response) => {
          this.toastr.success(response.message, "Login PIN Sent");
        },
        error: (error) => {
          this.authStatusListener.next(false);
        },
        complete: () => {
          console.log("Login request completed");
          this.router.navigate(["/login/verify-pin"], {
            queryParams: { email, rememberMe },
          });
        },
      });
  }

  VerifyLoginlogin(email: string, rememberMe: boolean, pin: string) {
    if (this.configService.areNotificationsDisabled()) {
      this.processLogin({ email, pin, rememberMe }, null);
      return;
    }

    this.fcmService = this.injector.get(FcmService);

    if ("Notification" in window) {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          this.fcmService.getToken()
            .then((fcmToken) => {
              console.log("FCM Token:", fcmToken);
              this.processLogin({ email, pin, rememberMe }, fcmToken);
            })
            .catch((error) => {
              console.error("Error getting FCM token:", error);
              this.processLogin({ email, pin, rememberMe }, null);
            });
        } else {
          console.warn("Notifications not granted. Logging in without FCM token.");
          this.processLogin({ email, pin, rememberMe }, null);
        }
      });
    } else {
      console.warn("Notifications API not supported. Logging in without FCM token.");
      this.processLogin({ email, pin, rememberMe }, null);
    }

  }


  private processLogin(authData: any, fcmToken: string | null) {
    const loginData = { ...authData, fcmToken: fcmToken };

    this.http
      .post<{
        token: string;
        expiresIn: number;
        userId: string;
        username: string;
        email: string;
        role: string;
        permission: any;
        profile_image: string;
        isPasswordChangeRequired: boolean;
      }>(this.getBackendURL() + "verifyLogin", loginData)
      .subscribe({
        next: (response) => {
          const token = response.token;
          this.token = token;
          if (token) {
            const expiresInDuration = response.expiresIn;
            const expirationDate = new Date(Date.now() + expiresInDuration * 1000);

            this.setAuthTimer(expiresInDuration);
            this.isAuthenticated = true;
            this.userId = response.userId;
            this.username = response.username;
            this.email = response.email;
            this.roleName = response.role;
            this.permission = response.permission;
            this.isPasswordChangeRequired = !!response.isPasswordChangeRequired;
            this.authStatusListener.next(true);

            this.saveAuthData(
              token,
              expirationDate,
              this.userId,
              this.username,
              this.email,
              this.roleName,
              authData.rememberMe,
              this.permission,
              this.isPasswordChangeRequired
            );
            this.clearSessionExpiredState();
            this.startInactivityTimer(true);

            if (this.isPasswordChangeRequired) {
              this.pendingProfileImageCheck = !response.profile_image;
              this.router.navigate(["/login/change-password"]);
              this.toastr.success("Signed in successfully", response.username);
              return;
            }

            this.router.navigate(["dashboard"]).then(() => {
              setTimeout(() => {
                if (!response.profile_image) {
                  this.openProfileImageDialog();
                }
              }, 100);
            });
            this.toastr.success("Signed in successfully", response.username);
          }
        },
        error: (error) => {
          console.error("Login failed:", error);
          this.authStatusListener.next(false);
        },
        complete: () => {
          console.log("Login request completed");
        },
      });
  }

  changeRequiredPassword(
    passwordCurrent: string,
    password: string,
    passwordConfirm: string
  ) {
    const payload = { passwordCurrent, password, passwordConfirm };
    const roleName = this.getRoleName();
    const roleQuery = roleName
      ? `&roleName=${encodeURIComponent(roleName)}`
      : "";

    return this.http.patch<{
      token: string;
      expiresIn: number;
      isPasswordChangeRequired: boolean;
    }>(
      this.getBackendURL() +
        `updateMyPassword?name=Update Password${roleQuery}`,
      payload
    ).pipe(
      tap((response) => {
        this.isPasswordChangeRequired = false;
        localStorage.setItem("isPasswordChangeRequired", "false");

        if (response?.token) {
          const expiresIn = response.expiresIn || 7 * 24 * 60 * 60;
          this.refreshTokenWithExpiry(response.token, expiresIn);
        }

        this.toastr.success("Password updated successfully");
        this.router.navigate(["dashboard"]).then(() => {
          setTimeout(() => {
            if (this.pendingProfileImageCheck) {
              this.openProfileImageDialog();
              this.pendingProfileImageCheck = false;
            }
          }, 100);
        });
      })
    );
  }

  updateToken(token: string, expirationDate: Date) {
    this.token = token;
    localStorage.setItem("token", token);
    localStorage.setItem("expiration", expirationDate.toISOString());
  }
  add(
    name: any,
    type: any,
    encounterId: any,
    applicantDigitalService: any,
    signature: string
  ) {
    let payload = {
      name: name,
      type: type,
      encounterId: encounterId,
      sign: signature,
      applicantDigitalService: applicantDigitalService,
    };
    return this.http.post(this.getBackendURL() + "acceptDigitalService/", payload);
  }

  validateConsentToken(token: string): Observable<ConsentValidateResponse> {
    return this.http.post<ConsentValidateResponse>(
      this.getBackendURL() + "consent/validate",
      { token }
    );
  }

  acceptConsentToken(token: string, signature: string) {
    return this.http.post(this.getBackendURL() + "consent/accept", {
      token,
      signature,
    });
  }

  getConsentPermalink(
    email: string,
    partyType: string,
    encounterId?: string,
    masterRef?: ConsentPermalinkMasterRef
  ) {
    const roleName = this.getRoleName();
    const roleQuery = roleName
      ? `?roleName=${encodeURIComponent(roleName)}`
      : "";
    const body: {
      email: string;
      partyType: string;
      encounterId?: string;
    } & ConsentPermalinkMasterRef = {
      email,
      partyType,
    };
    if (encounterId) {
      body.encounterId = encounterId;
    }
    if (masterRef?.attorneyId) {
      body.attorneyId = masterRef.attorneyId;
    }
    if (masterRef?.insuranceId) {
      body.insuranceId = masterRef.insuranceId;
    }
    if (masterRef?.applicantId) {
      body.applicantId = masterRef.applicantId;
    }
    return this.http.post<{
      url: string;
      lastSentAt: string;
      sentCount: number;
      invitationId: string;
    }>(this.getBackendURL() + "consent/permalink" + roleQuery, body);
  }

  openProfileImageDialog(): void {
    const dialogRef = this.dialog.open(ProfileImageDialogComponent, {
      width: "600px",
      disableClose: true,
      panelClass: "custom-dialog-container_profile_image",
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((selectedFile) => {
      if (selectedFile) {
        // console.log("File selected:", selectedFile);
      }
    });
  }

  recordConsentLinkCopied(invitationId: string, operationId: string) {
    return this.http.post(this.getBackendURL() + "consent/link-copied", { invitationId, operationId }, {
      params: { roleName: this.getRoleName() },
    });
  }

  sendMasterDigitalServiceRequest(
    masterType: "attorney" | "insurance" | "applicant",
    masterId: string,
    email?: string
  ) {
    const roleName = this.getRoleName();
    const roleQuery = roleName
      ? `?roleName=${encodeURIComponent(roleName)}`
      : "";
    const body: {
      masterType: string;
      masterId: string;
      email?: string;
    } = { masterType, masterId };
    if (email !== undefined && email !== null && String(email).trim()) {
      body.email = String(email).trim();
    }
    return this.http.post<{
      message: string;
      data: {
        masterType: string;
        masterId: string;
        partyType: string;
        digitalServiceStatus: string;
        email?: string;
      };
    }>(this.getBackendURL() + "consent/send-from-master" + roleQuery, body);
  }

  revokeMasterDigitalService(
    masterType: "attorney" | "insurance" | "applicant",
    masterId: string,
    revocationDocument: File
  ) {
    const roleName = this.getRoleName();
    const params = [`masterType=${encodeURIComponent(masterType)}`];
    if (roleName) params.push(`roleName=${encodeURIComponent(roleName)}`);
    const roleQuery = `?${params.join("&")}`;
    const formData = new FormData();
    formData.append("masterType", masterType);
    formData.append("masterId", masterId);
    formData.append("revocationDocument", revocationDocument);
    return this.http.post<{
      message: string;
      data: {
        masterType: string;
        masterId: string;
        digitalServiceStatus: string;
        driveFileId: string;
        driveFileName: string;
        revokedAt: string;
      };
    }>(this.getBackendURL() + "consent/revoke-from-master" + roleQuery, formData);
  }

  acceptMasterDigitalService(
    masterType: "attorney" | "insurance",
    masterId: string,
    externalAcceptanceUrl: string
  ) {
    const roleName = this.getRoleName();
    const roleQuery = roleName
      ? `?roleName=${encodeURIComponent(roleName)}`
      : "";
    return this.http.post<{
      message: string;
      data: {
        masterType: string;
        masterId: string;
        digitalServiceStatus: string;
        acceptanceMethod: string;
        externalAcceptanceUrl: string;
        previousStatus: string;
        recordedBy: string;
        recordedByName: string;
        recordedAt: string;
      };
    }>(this.getBackendURL() + "consent/accept-from-master" + roleQuery, {
      masterType,
      masterId,
      externalAcceptanceUrl,
    });
  }

  updateProfileImage(profile_image: File | string) {
    let roleName = this.getRoleName();
    const roleQuery = roleName
      ? `&roleName=${encodeURIComponent(roleName)}`
      : "";

    let Data = new FormData();
    Data.append("profile_image", profile_image);

    return this.http.put(
      this.getBackendURL() + `/update/profile?name=Update Profile Pic${roleQuery}`,
      Data
    );
  }

  fetchPermissions() {
    if (!this.userId) {
      console.error("User ID is not set, cannot fetch permissions.");
      return;
    }
    const roleName = this.getRoleName();
    this.http
      .get<{ permissions: any }>(
        this.getBackendURL() + "permissions/" + this.userId + "/" + roleName
      )
      .subscribe({
        next: (response) => {
          localStorage.setItem(
            "permission",
            JSON.stringify(response.permissions)
          );
        },
        error: (error) => {
          console.error("Error fetching permissions:", error);
        },
      });
  }

  changeRolePermisssion(userId: any, roleName: any) {
    this.http
      .get<{ permissions: any }>(
        this.getBackendURL() + "permissions/" + userId + "/" + roleName
      )
      .subscribe({
        next: async (response) => {
          // 1. Persist new role + permissions
          localStorage.setItem("roleName", roleName);
          localStorage.setItem(
            "permission",
            JSON.stringify(response.permissions)
          );

          // 2. Update in-memory state so getRoleName() is immediately correct
          this.roleName = roleName;

          // 3. Notify all subscribed components to re-evaluate permissions
          this.roleChanged$.next();

          // 4. Stay on current page if new role permits it; otherwise redirect to dashboard.
          if (!this.isCurrentRouteAccessible(response.permissions)) {
            window.location.href = "/dashboard";
          } else {
            window.location.reload();
          }
        },
        error: (error) => {
          console.error("Error fetching permissions:", error);
        },
      });
  }

  /**
   * Returns true when at least one permission entry in the given permissions
   * array covers the currently active route.
   *
   * The check mirrors the sidenav-item visibility logic:
   *  - For top-level routes  : match subComponentName === nav label for that route
   *  - For /dashboard        : always accessible (every role can reach home)
   *  - For /admin (profile)  : always accessible
   *
   * Route → nav-label map is derived from the navigation items defined in
   * app.component.ts so both places stay in sync.
   */
  /**
   * Helper to recursively scan the NavigationService items to find a label
   * corresponding to the active route path.
   */
  private getLabelForRoute(url: string, items: any[]): string | null {
    if (!Array.isArray(items)) return null;
    for (const item of items) {
      if (item.type === "link") {
        if (item.route && url.startsWith(item.route)) {
          return item.label;
        }
      } else if (
        (item.type === "dropdown" || item.type === "subheading") &&
        Array.isArray(item.children)
      ) {
        const found = this.getLabelForRoute(url, item.children);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  private isCurrentRouteAccessible(permissions: any[]): boolean {
    const currentUrl = this.router.url; // e.g. "/reports/oqme/editor"

    // Dashboard and profile pages are always accessible to any authenticated role.
    if (
      currentUrl.startsWith("/dashboard") ||
      currentUrl.startsWith("/admin")
    ) {
      return true;
    }

    // Recursively find the label for the current URL from dynamic navigation configuration
    const targetLabel = this.getLabelForRoute(
      currentUrl,
      this.navigationService.items
    );

    if (!targetLabel) {
      // Unknown / unmapped route — allow staying to avoid false redirects
      return true;
    }

    // Walk the permissions tree: look for any subComponent whose name matches
    // the label AND has at least one permission with isPermission === true.
    for (const component of permissions) {
      if (!Array.isArray(component.subComponent)) continue;
      for (const sub of component.subComponent) {
        if (sub.subComponentName === targetLabel) {
          if (targetLabel === "Digital Service") {
            return component.component === "Database" && Array.isArray(sub.permissions) && sub.permissions.some(
              (permission: any) => permission.name === "View Digital Service" && permission.isPermission === true
            );
          }
          const granted =
            Array.isArray(sub.permissions) &&
            sub.permissions.some((p: any) => p.isPermission === true);
          return granted;
        }
        // Special case: "View Encounters" lives inside the "Calendar" subComponent
        if (
          sub.subComponentName === "Calendar" &&
          targetLabel === "View Encounters"
        ) {
          const granted =
            Array.isArray(sub.permissions) &&
            sub.permissions.some(
              (p: any) =>
                p.name === "View Encounters" && p.isPermission === true
            );
          return granted;
        }
        // UI label "Report Print Queue" maps to RBAC key "USPS Print Queue"
        if (
          sub.subComponentName === "USPS Print Queue" &&
          targetLabel === "Report Print Queue"
        ) {
          const granted =
            Array.isArray(sub.permissions) &&
            sub.permissions.some((p: any) => p.isPermission === true);
          return granted;
        }
      }
    }

    // Label not found in permissions → not accessible
    return false;
  }

  logout() {
    this.invalidateSessionOnServer();
    this.stopInactivityTimer();
    this.token = null;
    this.isAuthenticated = false;
    this.authStatusListener.next(false);
    this.userId = null;
    (this.username = null), (this.roleName = null), (this.email = null);
    this.isPasswordChangeRequired = false;
    this.pendingProfileImageCheck = false;
    clearTimeout(this.tokenTimer);
    this.clearAuthData();
    this.router.navigate(["/"]);
    setTimeout(() => {
      this.toastr.success("Logout", "");
    }, 500);
  }

  // logout() {
  //   this.token = null;
  //   this.isAuthenticated = false;
  //   this.authStatusListener.next(false);
  //   this.userId = null;
  //   this.username = null;
  //   this.roleName = null;
  //   this.email = null;

  //   clearTimeout(this.tokenTimer);

  //   this.clearAuthData();

  //   this.router.navigate(["/"]);

  //   setTimeout(() => {
  //     this.toastr.info("Logged out", "", {
  //       timeOut: 2000,
  //     });
  //   }, 500);
  // }

  private clearAuthData() {
    localStorage.removeItem("token");
    localStorage.removeItem("expiration");
    localStorage.removeItem("userId");
    localStorage.removeItem("username");
    localStorage.removeItem("email");
    localStorage.removeItem("roleName");
    localStorage.removeItem("permission");
    localStorage.removeItem("isPasswordChangeRequired");
    localStorage.setItem("isAuthenticated", "false");
  }

  /**
   * On inactivity expiry the screen should stay visually unchanged (OL-256).
   * Only remove credentials needed to block API access; keep permission/role
   * data so the sidenav and toolbar continue to render until the user signs in.
   */
  private clearAuthDataForInactivityExpiry(): void {
    localStorage.removeItem("token");
    localStorage.removeItem("expiration");
    localStorage.setItem("isAuthenticated", "false");
  }

  /** Full cleanup when user chooses Sign In after an inactivity expiry. */
  clearAuthDataForSignInAfterExpiry(): void {
    this.userId = null;
    this.username = null;
    this.roleName = null;
    this.email = null;
    this.permission = null;
    this.clearAuthData();
  }

  private setAuthTimer(duration: number) {
    console.log("Setting timer: " + duration);
    this.tokenTimer = setTimeout(() => {
      this.logout();
    }, duration * 1000);
  }

  public refreshTokenWithExpiry(token: string, expiresIn: number) {
    const expirationDate = new Date(Date.now() + expiresIn * 1000);
    this.token = token;
    this.setAuthTimer(expiresIn);
    localStorage.setItem("token", token);
    localStorage.setItem("expiration", expirationDate.toISOString());
    this.startInactivityTimer();
  }

  private saveAuthData(
    token: string,
    expirationDate: Date,
    userId: string,
    username: string,
    email: string,
    roleName: string,
    rememberMe: boolean,
    permission: any,
    isPasswordChangeRequired: boolean
  ) {
    try {
      localStorage.setItem("token", token);
      localStorage.setItem("expiration", expirationDate.toISOString());
      localStorage.setItem("userId", userId);
      localStorage.setItem("username", username);
      localStorage.setItem("email", email);
      localStorage.setItem("roleName", roleName);
      localStorage.setItem("permission", JSON.stringify(permission));
      localStorage.setItem("isAuthenticated", "true");
      localStorage.setItem(
        "isPasswordChangeRequired",
        isPasswordChangeRequired ? "true" : "false"
      );
    } catch (e) {

      alert("⚠️ Warning: Local Storage is not available on this browser. Some features may not work.");
    }
  }


  private getAuthData() {
    const token = localStorage.getItem("token");
    const expirationDate = localStorage.getItem("expiration");
    const userId = localStorage.getItem("userId");
    const username = localStorage.getItem("username");
    const email = localStorage.getItem("email");
    const roleName = localStorage.getItem("roleName");
    const permission = localStorage.getItem("permission");

    if (!token || !expirationDate) {
      return;
    }

    return {
      token: token,
      expirationDate: new Date(expirationDate),
      userId: userId,
      username: username,
      email: email,
      roleName: roleName,
      permission: permission,
    };
  }

  updateFcmToken(newToken: string) {
    if (this.configService.areNotificationsDisabled()) {
      return;
    }

    if (!newToken) {
      console.warn("FCM Token is empty, skipping update");
      return;
    }

    // Check if user is authenticated
    if (!this.getIsAuth()) {
      console.warn("User not authenticated, skipping FCM token update");
      return;
    }

    // Check if config is loaded before making API call
    if (!this.configService.isConfigLoaded()) {
      console.warn("Config not loaded yet, waiting before updating FCM token");
      // Retry after a short delay
      setTimeout(() => {
        this.updateFcmToken(newToken);
      }, 500);
      return;
    }

    this.http.post(this.getBackendURL() + "updateFCM", {
      fcmToken: newToken,
    }).subscribe({
      next: (response) => {
        console.log("FCM Token Updated successfully");
      },
      error: (error) => {
        console.error("Failed to update FCM Token:", error);
        // Don't show error to user, just log it
      },
    });
  }

  autoAuthUser() {
    const authInformation = this.getAuthData();

    // Refresh after inactivity expiry: auth is cleared but sessionStorage flag
    // can persist — silently reset so login is a fresh entry (OL-387).
    if (this.isSessionExpiredDueToInactivity() && !authInformation) {
      this.clearSessionExpiredState();
      return;
    }

    if (!authInformation) {
      return;
    }
    const now = new Date();
    const expiresIn = authInformation.expirationDate.getTime() - now.getTime();
    if (expiresIn > 0) {
      this.token = authInformation.token;
      this.isAuthenticated = true;
      this.userId = authInformation.userId;
      this.username = authInformation.username;
      this.email = authInformation.email;
      this.roleName = authInformation.roleName;
      this.permission = authInformation.permission;
      this.isPasswordChangeRequired =
        localStorage.getItem("isPasswordChangeRequired") === "true";
      this.setAuthTimer(expiresIn / 1000);
      this.authStatusListener.next(true);

      this.fetchPermissions();

      this.reconcileLastActivityWithToken();
      if (this.isInactivityElapsed()) {
        // Cold start: user is not mid-session on a protected page (OL-387).
        this.expireSessionDueToInactivity(false);
        return;
      }

      this.startInactivityTimer(false);

      // FCM token update is handled by FcmService.initializeToken() and listenToTokenRefresh()
      // No need to call updateFcmTokenOnReload() here to avoid duplicate calls
      // this.router.navigate(['/dashboard']);
    }
  }

  forgotPassword(email: string) {
    const Email = { email: email };
    this.http
      .post<{ message: string; status: string }>(
        this.getBackendURL() + "forgotPassword",
        Email
      )
      .subscribe({
        next: (response) => {
          if (response.status === "success") {
            this.isLoadingsubject.next(true);
          }
          setTimeout(() => {
            this.router.navigate(["/check_email"]);
            this.toastr.info(
              "Successfully sent password reset link to your email.",
              "Reset Link"
            );
          }, 400);
        },
        error: (error) => {
          this.authStatusListener.next(false);
          // this.toastr.error(
          //   "Failed to send password reset link",
          //   error.message,
          //   {
          //     timeOut: 3000,
          //     closeButton: true,
          //   }
          // );
        },
        complete: () => {
          console.log("Forgot password request completed");
        },
      });
  }

  resetPassword(password: string, passwordConfirm: string, token: string) {
    const resetData = { password: password, passwordConfirm: passwordConfirm };
    this.http
      .post<{ data }>(this.getBackendURL() + "reset" + "/" + token, resetData)
      .subscribe((responce) => {
        this.toastr.success(
          "Your password is successfully reset.",
          "Successfully"
        );
        setTimeout(() => {
          this.router.navigate(["login"]);
        }, 300);
      });
  }

  isSessionExpiredDueToInactivity(): boolean {
    return sessionStorage.getItem(AuthService.SESSION_EXPIRED_KEY) === "true";
  }

  clearSessionExpiredState(): void {
    sessionStorage.removeItem(AuthService.SESSION_EXPIRED_KEY);
    this.sessionExpiredDialogOpen = false;
    this.removeSessionExpiredInteractionListeners();
  }

  /**
   * After a page refresh the expired flag can outlive cleared auth data.
   * Clean up and notify via toast so login is usable without a blocking modal.
   */
  reconcileExpiredSessionAfterRefresh(): void {
    if (!this.isSessionExpiredDueToInactivity()) {
      return;
    }

    this.clearSessionExpiredState();
    this.dialog.closeAll();
    this.toastr.warning(
      AuthService.SESSION_EXPIRED_MESSAGE,
      "Session Expired",
      {
        timeOut: 5000,
        closeButton: true,
      }
    );
  }

  handleServerSessionInactivityExpired(): void {
    if (this.isSessionExpiredDueToInactivity()) {
      return;
    }

    sessionStorage.setItem(AuthService.SESSION_EXPIRED_KEY, "true");
    this.stopInactivityTimer(false);
    clearTimeout(this.tokenTimer);
    this.token = null;
    this.isAuthenticated = false;
    this.authStatusListener.next(false);
    this.clearAuthDataForInactivityExpiry();
    this.registerSessionExpiredInteractionListeners();
  }

  showSessionExpiredDialog(): void {
    if (this.sessionExpiredDialogOpen) {
      return;
    }

    this.sessionExpiredDialogOpen = true;
    this.dialog
      .open(SessionExpiredDialogComponent, {
        width: "500px",
        disableClose: true,
        panelClass: "session-expired-dialog-container",
        autoFocus: false,
      })
      .afterClosed()
      .subscribe(() => {
        this.sessionExpiredDialogOpen = false;
      });
  }

  getLastActivityTimestamp(): number | null {
    const lastActivity = localStorage.getItem("lastActivity");
    if (!lastActivity) {
      return null;
    }

    const parsed = Number(lastActivity);
    return Number.isNaN(parsed) ? null : parsed;
  }

  /** JWT iat (ms) — used to detect stale lastActivity from a prior session/tab. */
  private getTokenIssuedAtMs(): number | null {
    const token = this.token || localStorage.getItem("token");
    if (!token) {
      return null;
    }

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload?.iat ? payload.iat * 1000 : null;
    } catch {
      return null;
    }
  }

  /**
   * If lastActivity predates the current JWT, it is leftover from an old session
   * (e.g. a background tab). Reset it instead of treating the user as idle.
   */
  private reconcileLastActivityWithToken(): void {
    const tokenIssuedAt = this.getTokenIssuedAtMs();
    if (!tokenIssuedAt) {
      return;
    }

    const lastActivity = this.getLastActivityTimestamp();
    if (!lastActivity || lastActivity < tokenIssuedAt) {
      const now = Date.now();
      localStorage.setItem("lastActivity", now.toString());
      this.lastStorageWrite = now;
    }
  }

  private isInactivityElapsed(): boolean {
    const lastActivity = this.getLastActivityTimestamp();
    if (!lastActivity) {
      return false;
    }

    return Date.now() - lastActivity >= this.inactivityTimeoutMs;
  }

  private invalidateSessionOnServer(): void {
    const token = this.token || localStorage.getItem("token");
    if (!token) {
      return;
    }

    this.http
      .post(this.getBackendURL() + "session/invalidate-inactivity", {})
      .subscribe({ error: () => {} });
  }

  private expireSessionDueToInactivity(armInteractionPrompt = true): void {
    if (armInteractionPrompt && this.isSessionExpiredDueToInactivity()) {
      return;
    }

    this.invalidateSessionOnServer();
    this.stopInactivityTimer(false);
    clearTimeout(this.tokenTimer);
    this.token = null;
    this.isAuthenticated = false;
    this.authStatusListener.next(false);
    this.isPasswordChangeRequired = false;
    this.pendingProfileImageCheck = false;
    this.clearAuthDataForInactivityExpiry();

    if (armInteractionPrompt) {
      sessionStorage.setItem(AuthService.SESSION_EXPIRED_KEY, "true");
      this.registerSessionExpiredInteractionListeners();
    } else {
      this.clearSessionExpiredState();
    }
  }

  private isPublicAuthRoute(): boolean {
    const url = this.router.url.split("?")[0];
    const publicPrefixes = [
      "/login",
      "/forgot-password",
      "/reset-password",
      "/check_email",
    ];
    return publicPrefixes.some(
      (prefix) => url === prefix || url.startsWith(`${prefix}/`)
    );
  }

  private handleSessionExpiredInteraction(event: Event): void {
    if (!this.isSessionExpiredDueToInactivity()) {
      return;
    }

    if (this.isPublicAuthRoute()) {
      this.clearSessionExpiredState();
      return;
    }

    const target = event.target as HTMLElement | null;
    if (
      target?.closest?.(
        ".session-expired-dialog-container, .session-expired-dialog"
      )
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.showSessionExpiredDialog();
  }

  private registerSessionExpiredInteractionListeners(): void {
    this.removeSessionExpiredInteractionListeners();

    const events = ["click", "keydown", "touchstart"];
    const handler = (event: Event) => this.handleSessionExpiredInteraction(event);

    events.forEach((eventName) => {
      window.addEventListener(eventName, handler, { capture: true });
    });

    this.sessionExpiredInteractionCleanup = () => {
      events.forEach((eventName) => {
        window.removeEventListener(eventName, handler, { capture: true } as any);
      });
    };
  }

  private removeSessionExpiredInteractionListeners(): void {
    if (this.sessionExpiredInteractionCleanup) {
      this.sessionExpiredInteractionCleanup();
      this.sessionExpiredInteractionCleanup = null;
    }
  }

  private startInactivityTimer(isFreshLogin = false) {
    this.stopInactivityTimer(false);
    if (this.isSessionExpiredDueToInactivity()) {
      return;
    }
    if (!this.getIsAuth()) return;

    const now = Date.now();
    if (isFreshLogin) {
      // Fresh login must reset activity before elapsed check — stale lastActivity
      // from a prior session would otherwise trigger immediate invalidate-inactivity.
      localStorage.setItem("lastActivity", now.toString());
      this.lastStorageWrite = now;
    } else if (!localStorage.getItem("lastActivity")) {
      localStorage.setItem("lastActivity", now.toString());
      this.lastStorageWrite = now;
    } else {
      this.reconcileLastActivityWithToken();
      if (this.isInactivityElapsed()) {
        this.expireSessionDueToInactivity();
        return;
      }
    }

    this.resetInactivityTimer(false);

    const handleActivity = () => this.resetInactivityTimer(true);

    const events = ["mousemove", "click", "keypress", "scroll", "touchstart"];
    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "lastActivity" && event.newValue) {
        this.resetInactivityTimer(false);
      }
    };
    window.addEventListener("storage", handleStorageChange);

    this.cleanupListeners = () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      window.removeEventListener("storage", handleStorageChange);
    };
  }

  private resetInactivityTimer(syncToStorage = true) {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }

    if (syncToStorage) {
      const now = Date.now();
      if (now - this.lastStorageWrite > this.storageWriteThrottleMs) {
        localStorage.setItem("lastActivity", now.toString());
        this.lastStorageWrite = now;
      }
    }

    this.inactivityTimer = setTimeout(() => {
      const lastActivity = Number(localStorage.getItem("lastActivity") || 0);
      const now = Date.now();
      const elapsed = now - lastActivity;
      if (elapsed >= this.inactivityTimeoutMs) {
        this.expireSessionDueToInactivity();
      } else {
        const remaining = this.inactivityTimeoutMs - elapsed;
        this.inactivityTimer = setTimeout(
          () => this.expireSessionDueToInactivity(),
          remaining
        );
      }
    }, this.inactivityTimeoutMs);
  }

  private stopInactivityTimer(removeLastActivity = true) {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    if (this.cleanupListeners) {
      this.cleanupListeners();
      this.cleanupListeners = null;
    }
    if (removeLastActivity) {
      localStorage.removeItem("lastActivity");
    }
  }
}
